import * as React from "react";
import { Tabs } from "../../../bits/Tabs/Tabs";
import styles from "./AppointmentSetting.module.scss";
import { Select } from "../../../bits/Select/Select";
import {
    IFeesCalculated,
    IClient,
    ReferralMethods,
    IAvailabilitySchedule,
    IReservedSchedule,
    DateFormats,
    ClientStatus,
} from "intake_shared/src/types";
import {
    TSaveClient,
    TUpdateClient,
    TUpdateClientByStatus,
    TUpdateMultipleClientFieldsByStatus,
} from "../../clientUiTypes";
import { AclContext } from "../../../../../contexts/AclContext";
import store from "../../../../../store";
import moment from "../../../../../utils/app_moment";
import { useToasts } from "react-toast-notifications";
import { Input } from "../../../bits/Input/Input";
import { Button } from "../../../bits/Button/Button";
import SaveCheckIcon from "../../../icons/SaveCheck";
import CancelIcon from "../../../icons/Cancel";
import InsuranceValues from "../../client-forms/insurance/InsuranceValues/InsuranceValues";
import CompleteClientFlow from "../../client-forms/CompleteClientFlow/CompleteClientFlow";
import ApptSettingTabWrapper from "./ApptSettingTabWrapper";
import { findLabelByPracticeId } from "../../../../../utils/utils";
import { CacheContext } from "../../../../../contexts/CacheContext";
import { getIntakeEmailSignature } from "../../../../../utils/intakeEmailSignature";
import { Badge } from "../../../bits/Badge/Badge";
import { getV2BadgePropsByPractice } from "../../../../../constants";
import { serviceDeliveryOptionLabels } from "intake_shared/src/constants";
import ClientInquiryPreferences from "../../ClientInquiryPreferences/ClientInquiryPreferences";
import { getAppointmentHeldValidationMessage } from "../../../../../utils/guardian";
import { resolveBookingRequestedService } from "../../../../v2utils";

type Option = {
    label: React.ReactNode;
    value: string;
};

const getConsentToSmsOptions = [
    { label: "Select option", value: "" },
    { label: "Yes", value: "Yes" },
    { label: "No", value: "No" },
];

const getReferralMethodOptions = [
    { label: "Select option", value: "" },
    ...Object.values(ReferralMethods)
        .sort((a, b) => a.localeCompare(b))
        .map((item) => ({
            label: item,
            value: item,
        })),
];

interface AppointmentSettingProps {
    client: IClient;
    updateClient: TUpdateClient;
    updateClientByStatus: TUpdateClientByStatus;
    updateMultipleClientFieldsByStatus: TUpdateMultipleClientFieldsByStatus;
    onSaveClient: TSaveClient;
}

export default function AppointmentSetting({
    client,
    updateClient,
    updateClientByStatus,
    updateMultipleClientFieldsByStatus,
    onSaveClient,
}: AppointmentSettingProps) {
    const { addToast } = useToasts();
    const [showOtherInput, setShowOtherInput] = React.useState(false);
    const olc = React.useContext(CacheContext);
    const allDepartmentsAndServices = olc?.allDepartmentAndServices || {};
    const requestedServices = olc?.requestedServices || {};
    const aclContext = React.useContext(AclContext);
    const [otherReferral, setOtherReferral] = React.useState("");
    const [showSaveButton, setShowSaveButton] = React.useState(false);
    const [feesCalculated, setFeesCalculated] = React.useState<IFeesCalculated>();
    const [autoFillFees, setAutoFillFees] = React.useState(false);
    const [reservedAppointments, setReservedAppointments] = React.useState<
        IAvailabilitySchedule[]
    >([]);

    const loggedInUserEmail = React.useContext(AclContext)?.authUser.user?.email;

    React.useEffect(() => {
        if (
            client.referred_by &&
            !Object.values(ReferralMethods).some(
                (method) => method.toUpperCase() === client.referred_by.toUpperCase()
            )
        ) {
            setShowOtherInput(true);
            setOtherReferral(client.referred_by);
        } else {
            setShowOtherInput(false);
        }
    }, [client.referred_by]);

    React.useEffect(() => {
        const fetchReservedAppointments = async (clientEmail: string) => {
            const appts = await store.fetchReservedAppointments(clientEmail);
            if (appts.length > 0) {
                setReservedAppointments(appts);
            }
        };
        fetchReservedAppointments(client.email);
    }, [client]);

    const handleReferralChange = (value) => {
        if (value === ReferralMethods.OTHER) {
            setShowOtherInput(true);
            setOtherReferral("");
            setShowSaveButton(true);
        } else {
            setShowOtherInput(false);
            setShowSaveButton(false);
            updateClient("referred_by", value);
        }
    };

    const handleSaveOtherReferral = async () => {
        if (otherReferral.trim() !== "") {
            await store.updateClientData({
                client_id: client.id,
                changes: { referred_by: otherReferral },
                last_updated_by: loggedInUserEmail as string,
            });
            setShowSaveButton(false);
            addToast("Referral method updated", {
                appearance: "success",
            });
        }
    };

    const handleCancelOtherReferral = () => {
        setShowOtherInput(false);
        setOtherReferral("");
        updateClient("referred_by", "");
    };

    const renderReservedAppointments = (appts: IReservedSchedule[]) => {
        return appts.map((r, idx) => {
            const appointmentPractice = findLabelByPracticeId(
                (r.practice_id as string) ||
                    (process.env.REACT_APP_MANTLE_PRACTICE_ID as string),
                allDepartmentsAndServices
            );
            const bookingRequestedService = resolveBookingRequestedService(
                r.availableService || "",
                client.requested_service,
                requestedServices
            );
            return (
                <div key={`appt-${r.id}-${idx}`} className={styles.appointmentItem}>
                    <div className={styles.infoRight}>
                        <div className={styles.clinicianInfo}>
                            <p className={styles.clinicianName}>{r.clinicianName}</p>
                            <Badge
                                content={appointmentPractice}
                                textColor={
                                    getV2BadgePropsByPractice(appointmentPractice)
                                        .textColor
                                }
                                bgColor={
                                    getV2BadgePropsByPractice(appointmentPractice)
                                        .bgColor
                                }
                            />
                        </div>
                        <div className={styles.appointmentInfo}>
                            <span className={styles.location}>{r.location} </span>
                            <div className={styles.appointmentInfoSeparator} />
                            <span className={styles.time}>
                                {moment(`${r.start}`).format(
                                    DateFormats.DAY_HOUR_FORMAT
                                )}{" "}
                            </span>
                            <div className={styles.appointmentInfoSeparator} />
                            <span className={styles.recurrenceRule}>
                                {r.recurrenceRule}
                            </span>
                            <div className={styles.appointmentInfoSeparator} />
                            <span className={styles.startingDate}>
                                Starting{" "}
                                {moment(`${r.start}`).format(
                                    DateFormats.DAY_MONTH_FORMAT
                                )}
                            </span>
                            {(olc?.requestedServices || {})[
                                r?.availableService || ""
                            ] && (
                                <>
                                    <div
                                        className={styles.appointmentInfoSeparator}
                                    />
                                    <span className={styles.requestedService}>
                                        {r?.availableService}
                                    </span>
                                </>
                            )}
                            <div className={styles.appointmentInfoSeparator} />
                            {
                                serviceDeliveryOptionLabels[
                                    r.serviceDeliveryOption || ""
                                ]
                            }
                        </div>
                    </div>
                    <Button
                        onClick={async () => {
                            const heldValidationMessage =
                                getAppointmentHeldValidationMessage(client);
                            if (heldValidationMessage) {
                                addToast(heldValidationMessage, {
                                    appearance: "error",
                                    autoDismiss: false,
                                });
                                return;
                            }

                            try {
                                if (!r.clinicianId || !r.id) {
                                    throw new Error(
                                        "No clinicianID or appointment id"
                                    );
                                }

                                const emailSignature = getIntakeEmailSignature(olc);
                                const success = await store.holdAppointment({
                                    clientId: client.id,
                                    clinicianId: r.clinicianId,
                                    availableWindowId: r.id,
                                    startingDate: moment(`${r.start}`).toDate(),
                                    clientRequestedCall: false,
                                    requestedService: bookingRequestedService,
                                    clientConcerns: "",
                                    clientCallNotes:
                                        client[ClientStatus.APPT_SETTING]
                                            ?.call_notes || "",
                                    serviceDeliveryOption:
                                        r.serviceDeliveryOption || "in_person",
                                    last_updated_by: aclContext?.userAcl.email!,
                                    practice_id: r?.practice_id as string,
                                    department:
                                        findLabelByPracticeId(
                                            (r?.practice_id as string) ||
                                                (process.env
                                                    .REACT_APP_MANTLE_PRACTICE_ID as string),
                                            allDepartmentsAndServices
                                        ) || "",
                                    signature: emailSignature,
                                });
                                if (!success) {
                                    throw new Error(
                                        "Failure when holding appointment"
                                    );
                                }
                                setReservedAppointments(
                                    reservedAppointments.filter(
                                        (ra) => ra.id !== r.id
                                    )
                                );
                                // Refresh the client data.
                                onSaveClient(client);
                                addToast(
                                    'Appointment held, view the held appointment on the "Appointments Held" tab',
                                    { appearance: "success" }
                                );
                            } catch (ex) {
                                console.error({ error: ex });
                                addToast("Failed to hold appointment", {
                                    appearance: "error",
                                });
                            }
                        }}
                    >
                        Hold appointment
                    </Button>
                </div>
            );
        });
    };

    const tabItems = [
        {
            key: "appt-details",
            label: "Appointment Details",
            children: (
                <ApptSettingTabWrapper client={client}>
                    <div className={styles.apptDetailsWrapper}>
                        {reservedAppointments.length ? (
                            <div className={styles.reservedAppointments}>
                                <p className={styles.reservedTitle}>
                                    Reserved Appointments
                                </p>
                                <div className={styles.reservedAppointmentsList}>
                                    {renderReservedAppointments(
                                        reservedAppointments
                                    )}
                                </div>
                            </div>
                        ) : (
                            <></>
                        )}
                        <div className={styles.apptDetailsContent}>
                            <div className="gridTwo">
                                {!showOtherInput ? (
                                    <Select
                                        label="Referral method"
                                        onChange={(val) =>
                                            handleReferralChange(
                                                (val as Option).value
                                            )
                                        }
                                        options={getReferralMethodOptions}
                                        defaultValue={getReferralMethodOptions.find(
                                            (method) =>
                                                method.value === client.referred_by
                                        )}
                                    />
                                ) : (
                                    <div className="flexReferral">
                                        <Input
                                            label="Referred by"
                                            value={otherReferral}
                                            onChange={(e) => {
                                                setOtherReferral(
                                                    e.target.value?.toUpperCase()
                                                );
                                                updateClient(
                                                    "referred_by",
                                                    e.target.value
                                                );
                                            }}
                                        />
                                        <div className="referralControls">
                                            {showSaveButton && (
                                                <div>
                                                    <Button
                                                        onClick={
                                                            handleSaveOtherReferral
                                                        }
                                                        disabled={
                                                            otherReferral.trim() ===
                                                            ""
                                                        }
                                                        variant="normal"
                                                    >
                                                        <SaveCheckIcon />
                                                    </Button>
                                                </div>
                                            )}
                                            <Button
                                                onClick={handleCancelOtherReferral}
                                                variant="normal"
                                                className="px-0"
                                            >
                                                <CancelIcon />
                                            </Button>
                                        </div>
                                    </div>
                                )}
                                <Select
                                    label="Consent to SMS"
                                    onChange={(val) =>
                                        updateClient(
                                            "consent_to_sms",
                                            (val as Option).value
                                        )
                                    }
                                    options={getConsentToSmsOptions}
                                    defaultValue={
                                        client.consent_to_sms
                                            ? getConsentToSmsOptions.find(
                                                  (option) =>
                                                      option.value ===
                                                      client.consent_to_sms
                                              )
                                            : ""
                                    }
                                />
                            </div>
                        </div>

                        <InsuranceValues
                            showLicFees={true}
                            client={client}
                            updateClientByStatus={updateClientByStatus}
                            updateClient={updateClient}
                            updateMultipleClientFieldsByStatus={
                                updateMultipleClientFieldsByStatus
                            }
                            setFeesCalculated={setFeesCalculated}
                            setAutoFillFees={setAutoFillFees}
                            feesCalculated={feesCalculated}
                            autoFillFees={autoFillFees}
                            viewingAppointmentSetting
                        />
                    </div>
                </ApptSettingTabWrapper>
            ),
        },
        {
            key: "close_client",
            label: "Close client",
            children: (
                <ApptSettingTabWrapper client={client}>
                    <CompleteClientFlow
                        title="Close client"
                        clientProp={client}
                        showAppointmentsToCancel={false}
                        expectedCaller="admin"
                        showCompletionReason
                        showCompletionNotes
                        onSaveClient={onSaveClient}
                    />
                </ApptSettingTabWrapper>
            ),
        },
    ];
    return (
        <div className={styles.appointmentSettingContainer}>
            <ClientInquiryPreferences client={client} />
            <Tabs items={tabItems} className={styles.apptSettingTabs} />
        </div>
    );
}
